const ALL_GOVERNANCE_FIELD_KEYS = ['owasp_mlp', 'owasp', 'ontology', 'internal_compliance', 'external_compliance'];

const isNonEmptyValue = (value: unknown): boolean => {
  if (value == null) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value as object).length > 0;
  return true;
};

const isWhitelistRuleShape = (value: unknown): boolean =>
  value != null &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  ('regulatory_body' in (value as object) || 'justification' in (value as object));

/** Normalize domain or project whitelist_rules API payloads into a rule-keyed record. */
export const normalizeWhitelistRulesRecord = (payload: unknown): Record<string, unknown> => {
  if (payload == null) return {};

  const unwrap = (raw: unknown): unknown => {
    if (raw == null || typeof raw !== 'object') return raw;
    const root = raw as Record<string, unknown>;
    if (root.whitelist_rules != null) return root.whitelist_rules;
    if (root.data != null && typeof root.data === 'object') return unwrap(root.data);
    return raw;
  };

  const unwrapped = unwrap(payload);

  if (Array.isArray(unwrapped)) {
    return unwrapped.reduce<Record<string, unknown>>((acc, item) => {
      if (item && typeof item === 'object' && !Array.isArray(item)) {
        Object.assign(acc, item as Record<string, unknown>);
      }
      return acc;
    }, {});
  }

  if (unwrapped && typeof unwrapped === 'object') {
    const record = unwrapped as Record<string, unknown>;
    if (Object.keys(record).some(key => isWhitelistRuleShape(record[key]))) {
      return record;
    }
  }

  return {};
};

/** Empty `{}` feature/collection buckets mean no datasets/models were analyzed yet. */
export const hasProjectFeatureAnalysisData = (payload: unknown): boolean => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return false;
  const root = payload as Record<string, unknown>;
  if (
    isNonEmptyValue(root.collection_results) ||
    isNonEmptyValue(root.feature_results) ||
    isNonEmptyValue(root.results)
  ) {
    return true;
  }
  return (
    isNonEmptyValue(root.fairness) ||
    isNonEmptyValue(root.fairness_metric) ||
    isNonEmptyValue(root.fairness_fairness_results)
  );
};

/** Prefer Redux, then RTK query payload, then project detail — scoped to the active project. */
export const resolveProjectResultsForId = (
  projectId: string,
  ...sources: unknown[]
): Record<string, unknown> | null => {
  for (const source of sources) {
    if (!source || typeof source !== 'object' || Array.isArray(source)) continue;
    const root = source as Record<string, unknown>;
    const sourceProjectId =
      (typeof root.projectId === 'string' && root.projectId) ||
      (typeof (root.project as Record<string, unknown> | undefined)?.projectId === 'string' &&
        String((root.project as Record<string, unknown>).projectId));
    if (sourceProjectId && sourceProjectId !== projectId) continue;
    return root;
  }
  return null;
};

const resolvePayloadProjectId = (payload: unknown): string | null => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null;
  const root = payload as Record<string, unknown>;
  if (typeof root.projectId === 'string' && root.projectId) return root.projectId;
  const nested = root.project as Record<string, unknown> | undefined;
  if (typeof nested?.projectId === 'string' && nested.projectId) return nested.projectId;
  return null;
};

const hasGovernanceFields = (bucket: Record<string, unknown>): boolean =>
  ALL_GOVERNANCE_FIELD_KEYS.some(key => isNonEmptyValue(bucket[key]));

const copyGovernanceFields = (
  source: Record<string, unknown>,
  target: Record<string, unknown> = {}
): Record<string, unknown> => {
  const next = { ...target };
  ALL_GOVERNANCE_FIELD_KEYS.forEach(key => {
    if (source[key] != null) {
      next[key] = source[key];
    }
  });
  return next;
};

/**
 * Build the `results` object Reviewer Studio tabs read.
 * Prefer API `results`; if empty, hydrate governance fields from the API payload once (not from tab lookups).
 */
export const normalizeReviewerStudioResults = (payload: unknown): Record<string, unknown> => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return {};
  }

  const root = payload as Record<string, unknown>;

  const tryBucket = (candidate: unknown): Record<string, unknown> | null => {
    if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return null;
    const bucket = candidate as Record<string, unknown>;
    return hasGovernanceFields(bucket) ? bucket : null;
  };

  const fromNestedResults =
    tryBucket(root.results) ||
    tryBucket((root.data as Record<string, unknown> | undefined)?.results) ||
    tryBucket((root.combined_llm_analysis as Record<string, unknown> | undefined)?.results);

  if (fromNestedResults) return fromNestedResults;

  if (hasGovernanceFields(root)) return root;

  const collection = root.collection_results;
  if (collection && typeof collection === 'object' && !Array.isArray(collection)) {
    return copyGovernanceFields(collection as Record<string, unknown>);
  }

  const directResults = root.results;
  if (directResults && typeof directResults === 'object' && !Array.isArray(directResults)) {
    return directResults as Record<string, unknown>;
  }

  return {};
};

/** Merge API payload into Redux shape — tabs only read `results` / `combined_llm_analysis.results`. */
export const buildReviewerStudioDisplayPayload = (
  apiPayload: Record<string, unknown>,
  existing?: unknown
): Record<string, unknown> => {
  const incomingProjectId = resolvePayloadProjectId(apiPayload);
  const existingProjectId = resolvePayloadProjectId(existing);
  const sameProject = !incomingProjectId || !existingProjectId || incomingProjectId === existingProjectId;

  const base =
    sameProject && existing && typeof existing === 'object' && !Array.isArray(existing)
      ? ({ ...(existing as Record<string, unknown>) } as Record<string, unknown>)
      : {};

  const existingResults = sameProject ? normalizeReviewerStudioResults(base) : {};
  const incomingResults = normalizeReviewerStudioResults(apiPayload);
  const mergedResults = { ...existingResults, ...incomingResults };

  const combined_llm_analysis = {
    ...(sameProject && typeof base.combined_llm_analysis === 'object' && !Array.isArray(base.combined_llm_analysis)
      ? (base.combined_llm_analysis as Record<string, unknown>)
      : {}),
    ...apiPayload,
    results: mergedResults
  };

  const preserveWhenIncomingEmpty = (key: string) => {
    if (!sameProject) return {};
    const incoming = apiPayload[key];
    const existingValue = base[key];
    if (isNonEmptyValue(incoming) || !isNonEmptyValue(existingValue)) return {};
    return { [key]: existingValue };
  };

  return {
    ...(sameProject ? base : {}),
    ...apiPayload,
    ...preserveWhenIncomingEmpty('fairness'),
    ...preserveWhenIncomingEmpty('fairness_metric'),
    ...preserveWhenIncomingEmpty('fairness_fairness_results'),
    results: mergedResults,
    combined_llm_analysis
  };
};

export const getReviewerStudioResults = (projectResults: unknown): Record<string, unknown> | null => {
  const normalized = normalizeReviewerStudioResults(projectResults);
  return hasGovernanceFields(normalized) ? normalized : null;
};

export const pickGovernanceEntry = (projectResults: unknown, aliases: string[]): unknown | null => {
  const bucket = getReviewerStudioResults(projectResults);
  if (!bucket) return null;

  for (const alias of aliases) {
    const value = bucket[alias];
    if (value == null) continue;
    const arr = Array.isArray(value) ? value : [value];
    if (arr.length > 0) return arr[0];
  }
  return null;
};

export const pickGovernanceRuns = (projectResults: unknown, alias: string): unknown[] | null => {
  const bucket = getReviewerStudioResults(projectResults);
  if (!bucket) return null;

  const value = bucket[alias];
  if (value == null) return null;
  return Array.isArray(value) ? value : [value];
};

export const pickLatestGovernanceRun = <T>(projectResults: unknown, alias: string): T | null => {
  const runs = pickGovernanceRuns(projectResults, alias);
  if (!runs || runs.length === 0) return null;

  const sorted = [...runs].sort((a, b) => {
    const ta = parseGovernanceTimestamp((a as { timestamp?: unknown })?.timestamp);
    const tb = parseGovernanceTimestamp((b as { timestamp?: unknown })?.timestamp);
    return tb - ta;
  });

  return (sorted[0] as T) ?? null;
};

const parseGovernanceTimestamp = (timestamp: unknown): number => {
  if (timestamp == null) return 0;
  if (typeof timestamp === 'object' && timestamp !== null && '$date' in (timestamp as object)) {
    const parsed = Date.parse(String((timestamp as { $date?: string }).$date ?? ''));
    return Number.isFinite(parsed) ? parsed : 0;
  }
  const parsed = Date.parse(String(timestamp));
  return Number.isFinite(parsed) ? parsed : 0;
};

/** @deprecated Use buildReviewerStudioDisplayPayload */
export const mergeCombinedLlmIntoProjectResults = (
  existing: unknown,
  combinedLlmPayload: Record<string, unknown>
): Record<string, unknown> => buildReviewerStudioDisplayPayload(combinedLlmPayload, existing);
